"use client"

import { useState, useEffect } from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import {
  IconLoader2,
  IconBell,
  IconBrandWhatsapp,
  IconUser,
  IconPhone,
  IconCalendar,
} from "@tabler/icons-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

import {
  getReminderTemplates,
  sendAppointmentReminder,
  previewAppointmentReminder,
} from "@/lib/client-reminders-api"
import type { Appointment, ReminderTemplate, ReminderPreview } from "@/types"

/**
 * The APPOINTMENT preview endpoint returns more than the shared
 * `ReminderPreview` shape describes: AppointmentReminderController::preview
 * also emits `recipient` (name/phone) and the raw `variables` map it will
 * substitute. Those two keys are appointment-only, so they are narrowed here
 * rather than widened on the shared type — and both are optional so a backend
 * that trims them degrades to hiding the lines instead of throwing.
 */
type AppointmentReminderPreview = ReminderPreview & {
  recipient?: { name: string; phone: string | null }
  variables?: Record<string, string>
}

interface SendReminderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  appointment: Appointment | null
  onSuccess?: () => void
}

export function SendReminderDialog({
  open,
  onOpenChange,
  appointment,
  onSuccess,
}: SendReminderDialogProps) {
  const t = useTranslations("appointments")
  const [templates, setTemplates] = useState<ReminderTemplate[]>([])
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("")
  const [isSending, setIsSending] = useState(false)
  const [preview, setPreview] = useState<AppointmentReminderPreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)

  // Load templates when dialog opens
  useEffect(() => {
    if (open) {
      loadTemplates()
      setSelectedTemplateId("")
      setPreview(null)
    }
  }, [open])

  // Load preview when template is selected
  useEffect(() => {
    if (selectedTemplateId && appointment) {
      loadPreview()
    } else {
      setPreview(null)
    }
  }, [selectedTemplateId, appointment])

  const loadTemplates = async () => {
    setIsLoadingTemplates(true)
    try {
      const data = await getReminderTemplates({ type: "appointment_reminder", active_only: true })
      setTemplates(data)
    } catch (error) {
      console.error("Failed to load templates:", error)
      toast.error(t("reminderDialog.toast.loadFailed"))
    } finally {
      setIsLoadingTemplates(false)
    }
  }

  const loadPreview = async () => {
    if (!appointment || !selectedTemplateId) return

    setIsLoadingPreview(true)
    try {
      const data = await previewAppointmentReminder(appointment.id, selectedTemplateId)
      setPreview(data)
    } catch (error) {
      console.error("Failed to load preview:", error)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handleSend = async () => {
    if (!appointment || !selectedTemplateId) return

    setIsSending(true)
    try {
      await sendAppointmentReminder(appointment.id, {
        template_id: selectedTemplateId,
      })
      toast.success(t("reminderDialog.toast.sent"))
      onOpenChange(false)
      onSuccess?.()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || t("reminderDialog.toast.sendFailed"))
    } finally {
      setIsSending(false)
    }
  }

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  }

  if (!appointment) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconBell className="h-5 w-5" />
            {t("reminderDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("reminderDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Appointment Info */}
          <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <IconCalendar className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{appointment.display_title}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconUser className="h-4 w-4" />
              <span>{appointment.lead?.full_name || t("reminderDialog.noLead")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconPhone className="h-4 w-4" />
              <span>{appointment.lead?.phone || t("reminderDialog.noPhone")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <IconCalendar className="h-4 w-4" />
              <span>{formatDateTime(appointment.scheduled_at)}</span>
            </div>
          </div>

          {/* Template Selection */}
          <div className="space-y-2">
            <Label>{t("reminderDialog.selectTemplate")}</Label>
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <IconLoader2 className="h-4 w-4 animate-spin" />
                {t("reminderDialog.loadingTemplates")}
              </div>
            ) : templates.length === 0 ? (
              <Alert>
                <IconBrandWhatsapp className="h-4 w-4" />
                <AlertTitle>{t("reminderDialog.noTemplatesTitle")}</AlertTitle>
                <AlertDescription>
                  {t("reminderDialog.noTemplatesBody")}
                </AlertDescription>
              </Alert>
            ) : (
              <Select
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("reminderDialog.templatePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} ({template.language.toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Preview */}
          {isLoadingPreview ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/50 rounded-lg">
              <IconLoader2 className="h-4 w-4 animate-spin" />
              {t("reminderDialog.loadingPreview")}
            </div>
          ) : preview ? (
            <div className="space-y-2">
              <Label>{t("reminderDialog.messagePreview")}</Label>
              <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg">
                <div className="flex items-start gap-2">
                  <IconBrandWhatsapp className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="space-y-1">
                    {preview.recipient && (
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        {t("reminderDialog.previewTo", {
                          name: preview.recipient.name,
                          phone: preview.recipient.phone ?? "",
                        })}
                      </p>
                    )}
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {t("reminderDialog.previewTemplate", { name: preview.template_name })}
                    </p>
                    {preview.variables && Object.keys(preview.variables).length > 0 && (
                      <div className="text-xs text-green-600 dark:text-green-400">
                        <span className="font-medium">{t("reminderDialog.previewVariables")}</span>
                        {Object.entries(preview.variables)
                          .map(([key, value]) => `${key}=${value}`)
                          .join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Phone Warning */}
          {appointment.lead && !appointment.lead.phone && (
            <Alert variant="destructive">
              <AlertTitle>{t("reminderDialog.phoneWarningTitle")}</AlertTitle>
              <AlertDescription>
                {t("reminderDialog.phoneWarningBody")}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            {t("reminderDialog.cancel")}
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              isSending ||
              !selectedTemplateId ||
              !appointment.lead?.phone ||
              templates.length === 0
            }
          >
            {isSending ? (
              <>
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("reminderDialog.sending")}
              </>
            ) : (
              <>
                <IconBrandWhatsapp className="mr-2 h-4 w-4" />
                {t("reminderDialog.sendReminder")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
